import React, { useState } from "react";
import { toast } from "sonner";
import { Users, Home, ShoppingBag, Gift, Star } from "lucide-react";
import { waLink } from "../config";
import { requestVentePrivee } from "../lib/api";
import { WhatsAppIcon } from "../components/WhatsAppButton";
import Reveal from "../components/Reveal";

const STEPS = [
  { icon: Users, t: "Vous invitez vos amies", d: "Réunissez 4 à 10 amies chez vous, dans la bonne humeur." },
  { icon: Home, t: "Esther vient chez vous", d: "J'arrive avec une sélection de robes, bijoux, lingerie et accessoires." },
  { icon: ShoppingBag, t: "Vous profitez et commandez", d: "Essayez, comparez et repartez avec vos coups de cœur." },
];

export default function VentesPrivees() {
  const [form, setForm] = useState({ name: "", commune: "", whatsapp: "", date: "", guests: "", message: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try { await requestVentePrivee(form); toast.success("Demande envoyée ! Esther vous recontacte très vite."); setForm({ name: "", commune: "", whatsapp: "", date: "", guests: "", message: "" }); }
    catch { toast.error("Une erreur est survenue"); }
  };
  return (
    <div data-testid="ventes-privees-page">
      <section className="relative h-[60vh] min-h-[420px] flex items-center justify-center text-center">
        <img src="https://images.pexels.com/photos/18731383/pexels-photo-18731383.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000" alt="Soirée shopping" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative px-6">
          <h1 className="font-display text-4xl md:text-6xl text-white">Soirées Shopping à Domicile</h1>
          <p className="font-serif-el italic text-dore text-xl mt-3">Vivez une expérience mode unique entre amies en Guadeloupe</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 py-16 text-center">
        <p className="font-serif-el text-xl md:text-2xl text-ardoise leading-relaxed">Organisez votre soirée shopping à domicile avec vos amies. Esther vient chez vous avec toute la boutique. Essayez nos pièces dans une ambiance conviviale et chaleureuse. <b className="text-encre">Cadeau offert à l'hôtesse !</b></p>
      </section>

      <section className="bg-white py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-3xl md:text-4xl text-center text-encre mb-12">Comment ça marche ?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {STEPS.map((s, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-rose/40 flex items-center justify-center mx-auto mb-4 relative">
                    <s.icon size={26} className="text-encre" />
                    <span className="absolute -top-2 -right-1 w-6 h-6 bg-dore text-white text-xs rounded-full flex items-center justify-center font-semibold">{i + 1}</span>
                  </div>
                  <h3 className="font-display text-xl text-encre mb-2">{s.t}</h3>
                  <p className="text-sm text-ardoise">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-10">
        <div className="bg-rose/30 p-8">
          <Gift size={30} className="text-dore mb-3" />
          <h3 className="font-display text-2xl text-encre mb-3">Avantages hôtesse</h3>
          <ul className="space-y-2 text-sm text-ardoise">
            <li>🎁 Un cadeau surprise offert</li>
            <li>💫 Une réduction exclusive sur vos achats</li>
            <li>👗 Un moment privilégié avec vos amies</li>
            <li>✨ Des conseils mode personnalisés</li>
          </ul>
        </div>
        <div className="bg-noir text-white p-8">
          <Star size={30} className="text-dore mb-3" />
          <h3 className="font-display text-2xl text-white mb-3">Ce que vous trouverez</h3>
          <ul className="space-y-2 text-sm text-white/70">
            <li>• Robes d'été, de soirée et longues</li>
            <li>• Bijoux dorés & parures</li>
            <li>• Lingerie et nuisettes satin</li>
            <li>• Maillots de bain & accessoires</li>
          </ul>
        </div>
      </section>

      <section className="max-w-2xl mx-auto px-6 pb-20">
        <h2 className="font-display text-3xl text-center text-encre mb-2">Demander ma soirée shopping</h2>
        <p className="text-center text-ardoise text-sm mb-8">Remplissez le formulaire, Esther vous recontacte pour organiser votre soirée.</p>
        <form onSubmit={submit} className="space-y-4 bg-white border border-dore/20 p-8" data-testid="vente-privee-form">
          <input required placeholder="Prénom et nom" value={form.name} onChange={set("name")} className="w-full border border-dore/30 px-4 py-3 text-sm outline-none focus:border-dore" data-testid="vp-name" />
          <div className="grid sm:grid-cols-2 gap-4">
            <input required placeholder="Commune en Guadeloupe" value={form.commune} onChange={set("commune")} className="w-full border border-dore/30 px-4 py-3 text-sm outline-none focus:border-dore" data-testid="vp-commune" />
            <input required placeholder="Numéro WhatsApp" value={form.whatsapp} onChange={set("whatsapp")} className="w-full border border-dore/30 px-4 py-3 text-sm outline-none focus:border-dore" data-testid="vp-whatsapp" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input type="date" value={form.date} onChange={set("date")} className="w-full border border-dore/30 px-4 py-3 text-sm outline-none focus:border-dore" data-testid="vp-date" />
            <input placeholder="Nombre d'invitées estimé" value={form.guests} onChange={set("guests")} className="w-full border border-dore/30 px-4 py-3 text-sm outline-none focus:border-dore" data-testid="vp-guests" />
          </div>
          <textarea rows={3} placeholder="Message libre (optionnel)" value={form.message} onChange={set("message")} className="w-full border border-dore/30 px-4 py-3 text-sm outline-none focus:border-dore" />
          <button className="w-full bg-noir text-white py-3.5 text-xs uppercase tracking-widest font-semibold hover:bg-ardoise transition-colors" data-testid="vp-submit">Demander ma soirée shopping</button>
          <a href={waLink("Bonjour Esther ! Je souhaite organiser une soirée shopping à domicile.")} target="_blank" rel="noreferrer" className="w-full flex items-center justify-center gap-2 bg-whatsapp text-white py-3.5 text-xs uppercase tracking-widest font-semibold hover:brightness-95 transition-all"><WhatsAppIcon size={15} /> Ou demander sur WhatsApp</a>
        </form>
      </section>
    </div>
  );
}
